import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material'
import { Close, TrendingUp, CalendarToday, Info } from '@mui/icons-material'
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded'
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded'
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded'
import { useAuth } from '../../context/AuthContext'
import { useNavigate } from 'react-router-dom'

/* ─── Date helper ───────────────────────────────────────────────────────── */
const convertArrayToDate = (dateInput) => {
  if (!dateInput) return null

  if (typeof dateInput === 'string') {
    const date = new Date(dateInput)
    if (isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  if (!Array.isArray(dateInput) || dateInput.length < 3) return null
  const [year, month, day, hour = 0, minute = 0, second = 0, nanosecond = 0] =
    dateInput
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null

  const date = new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      hour,
      minute,
      second,
      Math.floor(nanosecond / 1000000),
    ),
  )
  if (isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/* ─── Quota Metric ──────────────────────────────────────────────────────── */
const QuotaMetric = ({ icon, label, remaining, limit, showBar }) => {
  const pct = limit > 0 ? (remaining / limit) * 100 : 0
  const clampedPct = Math.max(0, Math.min(100, pct))

  const getQuotaBarColor = (percentage, remainingUnits) => {
    if (remainingUnits <= 0 || percentage <= 0) return '#e53935' // red
    if (percentage <= 20) return '#ff8f00' // amber
    if (percentage <= 50) return '#ffda1b' // yellow
    return '#43af76' // green
  }

  const barColor = getQuotaBarColor(pct, remaining)

  return (
    <Tooltip title={`${remaining} of ${limit} ${label} remaining`} arrow>
      {/* Outer wrapper — column: top row + progress bar below */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.55,
          minWidth: 0,
        }}
      >
        {/* ── Single line: icon · label · big-number/limit ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Icon */}
          {React.cloneElement(icon, {
            sx: {
              fontSize: { xs: 14, sm: 15, md: 16 },
              opacity: 0.8,
              flexShrink: 0,
            },
          })}

          {/* Label */}
          <Typography
            sx={{
              fontSize: { xs: '0.72rem', sm: '0.78rem', md: '0.82rem' },
              fontWeight: 500,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              color: 'rgba(255,255,255,0.85)',
            }}
          >
            {label}
          </Typography>

          {/* Count: big remaining + small /limit */}
          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
              fontWeight: 800,
              lineHeight: 1,
              color: '#fff',
              whiteSpace: 'nowrap',
              ml: 0.4,
            }}
          >
            {remaining}
            <Typography
              component='span'
              sx={{
                fontSize: { xs: '0.68rem', sm: '0.72rem' },
                fontWeight: 400,
                opacity: 0.6,
              }}
            >
              /{limit}
            </Typography>
          </Typography>
        </Box>

        {showBar && (
          <Box sx={{ position: 'relative', width: '100%', height: 6 }}>
            {/* Track */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.18)',
                overflow: 'hidden',
              }}
            >
              {/* Fill */}
              <Box
                sx={{
                  height: '100%',
                  width: `${clampedPct}%`,
                  bgcolor: barColor,
                  borderRadius: 3,
                  transition: 'width 0.6s ease',
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Tooltip>
  )
}

/* Banner  */
const QuotaStatusBanner = () => {
  const { quotaStatus, isQuotaLow } = useAuth()
  const [dismissed, setDismissed] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()

  if (!quotaStatus || dismissed) return null

  const {
    hasActiveSubscription,
    remainingLinkClicks,
    monthlyLinkClicksLimit,
    remainingDtaaViews,
    monthlyDtaaViewsLimit,
    quotaResetDate,
    activeSubscriptions,
  } = quotaStatus

  const isExhausted = remainingLinkClicks === 0 && remainingDtaaViews === 0
  const showUpgrade = !hasActiveSubscription || isQuotaLow()
  const showBar = !hasActiveSubscription
  const activePlanCount = hasActiveSubscription
    ? Math.max(activeSubscriptions?.length || 0, 1)
    : 0
  const activePlanLabel = `${activePlanCount} Active Plan${activePlanCount > 1 ? 's' : ''}`
  const goToSubscriptions = () => navigate('/profile?tab=subscriptions')
  const constantBannerBackground =
    'linear-gradient(135deg, rgba(35, 37, 54, 0.98) 0%, rgba(35, 37, 54, 0.94) 100%)'
  const fixedTintOverlay = 'rgba(255, 255, 255, 0.03)'

  /* ── Colour themes ── */
  const ct = {
    bg: constantBannerBackground,
    border: 'rgba(255,255,255,0.12)',
    shadow: '0 4px 12px rgba(0,0,0,0.15)',
    divider: 'rgba(255,255,255,0.18)',
    buttonBg: '#43af76',
    buttonColor: 'white',
    buttonHover: '#3a9668',
  }

  /* ── Reusable sub-pieces ── */
  const ActiveBadge = () =>
    hasActiveSubscription ? (
      <Chip
        icon={<TrendingUp sx={{ fontSize: { xs: 13, sm: 14 }, color: '#fff' }} />}
        label={activePlanLabel}
        size='small'
        clickable
        onClick={goToSubscriptions}
        sx={{
          bgcolor: 'rgba(255,255,255,0.22)',
          color: 'white',
          fontWeight: 700,
          fontSize: { xs: '0.68rem', sm: '0.72rem' },
          height: { xs: 24, sm: 26 },
          border: '1px solid rgba(255,255,255,0.3)',
          flexShrink: 0,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.28)',
            borderColor: 'rgba(255,255,255,0.42)',
          },
          '& .MuiChip-label': {
            px: { xs: 1, sm: 1.25 },
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
          },
          '& .MuiChip-icon': {
            color: '#fff !important',
            opacity: 1,
          },
        }}
      />
    ) : null

  const ResetDatePill = () =>
    !hasActiveSubscription ? (
      <Tooltip title='Your free quota resets on this date' arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            px: { xs: 1, sm: 1.1 },
            py: { xs: 0.65, sm: 0.65 },
            bgcolor: 'rgba(0,0,0,0.28)',
            borderRadius: 1.5,
            border: '1px solid rgba(255,255,255,0.1)',
            flexShrink: 0,
          }}
        >
          <CalendarToday sx={{ fontSize: { xs: 14, sm: 15 }, flexShrink: 0 }} />
          <Box>
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: '0.63rem' },
                opacity: 0.72,
                lineHeight: 1,
                mb: 0.2,
              }}
            >
              Resets
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.76rem', sm: '0.78rem' },
                fontWeight: 700,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {convertArrayToDate(quotaResetDate) || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    ) : null

  const UpgradeButton = () =>
    showUpgrade ? (
      <Button
        variant='contained'
        size='small'
        startIcon={<PaymentRoundedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
        onClick={() => navigate('/pricing')}
        sx={{
          bgcolor: ct.buttonBg,
          color: ct.buttonColor,
          fontWeight: 700,
          fontSize: { xs: '0.75rem', sm: '0.78rem', md: '0.82rem' },
          px: { xs: 1.75, sm: 2, md: 2.5 },
          py: { xs: 0.6, sm: 0.65 },
          borderRadius: 2,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
          letterSpacing: '0.02em',
          flexShrink: 0,
          '&:hover': {
            bgcolor: ct.buttonHover,
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.22)',
          },
          transition: 'all 0.18s ease',
        }}
      >
        {isMobile ? 'Upgrade Now' : 'Upgrade Now'}
      </Button>
    ) : null

  const CloseBtn = () => (
    <IconButton
      size='small'
      onClick={() => setDismissed(true)}
      sx={{
        color: 'white',
        opacity: 0.85,
        bgcolor: 'rgba(255,255,255,0.13)',
        width: { xs: 30, sm: 28 },
        height: { xs: 30, sm: 28 },
        flexShrink: 0,
        '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.22)' },
      }}
    >
      <Close sx={{ fontSize: { xs: 15, sm: 16 } }} />
    </IconButton>
  )

  return (
    <Box
      sx={{
        background: ct.bg,
        color: 'white',
        borderBottom: `1px solid ${ct.border}`,
        position: 'sticky',
        top: { xs: 56, sm: 64 },
        zIndex: 1100,
        boxShadow: ct.shadow,
        backdropFilter: 'blur(10px) saturate(115%)',
        WebkitBackdropFilter: 'blur(10px) saturate(115%)',
        isolation: 'isolate',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundColor: fixedTintOverlay,
          pointerEvents: 'none',
          zIndex: 0,
        },
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth='lg' sx={{ py: { xs: 0.9, sm: 1, md: 1.1 } }}>
        {/*
            MOBILE layout  (xs)
       */}
        {isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0,
                minWidth: 0,
              }}
            >
              {/* Active badge */}
              {hasActiveSubscription && (
                <>
                  <Chip
                    icon={<TrendingUp sx={{ fontSize: 10, color: '#fff' }} />}
                    label={activePlanLabel}
                    size='small'
                    clickable
                    onClick={goToSubscriptions}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.22)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.70rem',
                      height: 23,
                      border: '1px solid rgba(255,255,255,0.3)',
                      flexShrink: 0,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.28)',
                        borderColor: 'rgba(255,255,255,0.42)',
                      },
                      '& .MuiChip-label': {
                        px: 1,
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                      },
                      '& .MuiChip-icon': {
                        color: '#fff !important',
                        opacity: 1,
                      },
                    }}
                  />
                  <Divider
                    orientation='vertical'
                    flexItem
                    sx={{ borderColor: ct.divider, mx: 0.6, my: 0.2 }}
                  />
                </>
              )}

              {/* Links */}
              <Tooltip
                title={`${remainingLinkClicks} of ${monthlyLinkClicksLimit} link clicks remaining`}
                arrow
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <InsertLinkRoundedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.88)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Links
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 800,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      ml: 0.2,
                    }}
                  >
                    {remainingLinkClicks}
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '0.72rem',
                        opacity: 0.6,
                        fontWeight: 400,
                      }}
                    >
                      /{monthlyLinkClicksLimit}
                    </Typography>
                  </Typography>
                </Box>
              </Tooltip>

              <Divider
                orientation='vertical'
                flexItem
                sx={{ borderColor: ct.divider, mx: 0.6, my: 0.25 }}
              />

              {/* DTAAs */}
              <Tooltip
                title={`${remainingDtaaViews} of ${monthlyDtaaViewsLimit} DTAA views remaining`}
                arrow
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <ArticleRoundedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.88)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DTAAs
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      fontWeight: 800,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      ml: 0.2,
                    }}
                  >
                    {remainingDtaaViews}
                    <Typography
                      component='span'
                      sx={{
                        fontSize: '0.72rem',
                        opacity: 0.6,
                        fontWeight: 400,
                      }}
                    >
                      /{monthlyDtaaViewsLimit}
                    </Typography>
                  </Typography>
                </Box>
              </Tooltip>

              {/* Reset date inline — free only */}
              {!hasActiveSubscription && (
                <>
                  <Divider
                    orientation='vertical'
                    flexItem
                    sx={{ borderColor: ct.divider, mx: 1.25, my: 0.25 }}
                  />
                  <Tooltip title='Your free quota resets on this date' arrow>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.4,
                        flexShrink: 0,
                        px: { xs: 0.85, sm: 1.25 },
                        py: { xs: 0.5, sm: 0.75 },
                        bgcolor: 'rgba(0,0,0,0.28)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(255,255,255,0.18)',
                      }}
                    >
                      <CalendarToday sx={{ fontSize: 14 }} />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            opacity: 0.7,
                            lineHeight: 1,
                            mb: 0.1,
                          }}
                        >
                          Resets
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: '#fff',
                            whiteSpace: 'nowrap',
                            lineHeight: 1,
                          }}
                        >
                          {convertArrayToDate(quotaResetDate) || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Tooltip>
                </>
              )}

              {/* Spacer */}
              <Box sx={{ flex: 1 }} />

              {/* Close */}
              <CloseBtn />
            </Box>

            {/* ── Full-width Upgrade button (free only) ── */}
            {showUpgrade && (
              <Button
                variant='contained'
                fullWidth
                startIcon={<PaymentRoundedIcon />}
                onClick={() => navigate('/pricing')}
                sx={{
                  bgcolor: ct.buttonBg,
                  color: ct.buttonColor,
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  py: 0.75,
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
                  letterSpacing: '0.03em',
                  '&:hover': {
                    bgcolor: ct.buttonHover,
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.22)',
                  },
                  transition: 'all 0.18s ease',
                }}
              >
                Upgrade Now
              </Button>
            )}
          </Box>
        )}

        {/* Tablet / Desktop layout  (sm+)*/}
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { sm: 1.5, md: 2 },
            }}
          >
            <ActiveBadge />

            <QuotaMetric
              icon={<InsertLinkRoundedIcon />}
              label='Links'
              remaining={remainingLinkClicks}
              limit={monthlyLinkClicksLimit}
              showBar={showBar}
            />

            <Divider
              orientation='vertical'
              flexItem
              sx={{ borderColor: ct.divider, my: 0.25 }}
            />

            <QuotaMetric
              icon={<ArticleRoundedIcon />}
              label='DTAAs'
              remaining={remainingDtaaViews}
              limit={monthlyDtaaViewsLimit}
              showBar={showBar}
            />

            {!hasActiveSubscription && (
              <Divider
                orientation='vertical'
                flexItem
                sx={{ borderColor: ct.divider, my: 0.25 }}
              />
            )}

            <ResetDatePill />

            {/* Spacer pushes actions to the right */}
            <Box sx={{ flex: 1 }} />

            <UpgradeButton />
            <CloseBtn />
          </Box>
        )}

        {/* Warning strip  */}
        {isQuotaLow() && !hasActiveSubscription && (
          <Box
            sx={{
              mt: { xs: 0.75, sm: 0.85 },
              px: { xs: 0.85, sm: 1.25 },
              py: { xs: 0.6, sm: 0.75 },
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 1.5,
              border: '1px solid rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.75,
            }}
          >
            <Info
              sx={{
                fontSize: { xs: 14, sm: 15 },
                opacity: 0.9,
                flexShrink: 0,
                mt: '1px',
              }}
            />
            <Typography
              sx={{
                fontSize: { xs: '0.67rem', sm: '0.71rem' },
                lineHeight: 1.5,
                opacity: 0.95,
              }}
            >
              {isExhausted
                ? 'You have no remaining quota for this month. Links are now disabled and Tax Treaties are locked. Please upgrade to continue accessing all features.'
                : 'Your quota for this month is running low. Consider upgrading.'}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default QuotaStatusBanner
